import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Eye, EyeOff, Key, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { alterarSenhaCliente } from '../services/api';

const ChangePassword = () => {
  const [passwords, setPasswords] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    new_password: false,
    confirm_password: false
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  // Dados vindos da verificação de documento
  const { userId, document } = location.state || {};

  useEffect(() => {
    // Se não tiver dados da verificação, redirecionar
    if (!userId) {
      navigate('/check-document');
    }
  }, [userId, navigate]);

  const handlePasswordChange = (field, value) => {
    setPasswords(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpar erro do campo quando usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Validar senhas em tempo real
    if (field === 'confirm_password' || (field === 'new_password' && passwords.confirm_password)) {
      const newPass = field === 'new_password' ? value : passwords.new_password;
      const confirmPass = field === 'confirm_password' ? value : passwords.confirm_password;
      
      if (confirmPass && newPass !== confirmPass) {
        setErrors(prev => ({
          ...prev,
          confirm_password: 'As senhas não são idênticas. Corrija, por favor.'
        }));
      } else {
        setErrors(prev => ({
          ...prev,
          confirm_password: ''
        }));
      }
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validatePasswords = () => {
    const newErrors = {};

    if (!passwords.new_password) {
      newErrors.new_password = 'Nova senha é obrigatória';
    } else if (passwords.new_password.length < 6) {
      newErrors.new_password = 'A senha deve ter pelo menos 6 caracteres';
    }

    if (!passwords.confirm_password) {
      newErrors.confirm_password = 'Confirmação de senha é obrigatória';
    } else if (passwords.new_password !== passwords.confirm_password) {
      newErrors.confirm_password = 'As senhas não são idênticas. Corrija, por favor.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdatePassword = async () => {
    if (!validatePasswords()) return;

    setLoading(true);
    setSuccess(false);

    try {
      // Chamar API para alterar senha (envia em texto puro, API armazena assim)
      await alterarSenhaCliente(userId, passwords.new_password);
      
      setSuccess(true);
      
      toast({
        title: "Senha alterada com sucesso!",
        description: "Sua senha foi alterada. Redirecionando para o login...",
        duration: 3000,
      });

      // Aguardar um pouco antes de redirecionar
      setTimeout(() => {
        navigate('/login');
      }, 3000);

    } catch (err) {
      console.error('Erro ao alterar senha:', err);
      toast({
        title: "Erro ao alterar senha",
        description: err.message || "Ocorreu um erro. Tente novamente.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!userId) {
    return null; // Componente será redirecionado
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        {/* Logo e título */}
        <div className="text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_knorr-auction-proxy/artifacts/ud6gmi7h_Screenshot_7-removebg-preview.png"
            alt="Knorr Leilões"
            className="h-16 w-auto mx-auto mb-4"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          />
          <h1 className="text-4xl font-bold text-[rgb(46,48,146)] mb-2 hidden">
            KNORR LEILÕES
          </h1>
          <p className="text-gray-600">Nova Senha</p>
        </div>

        <Card className="border-2 border-gray-200">
          <CardHeader className="text-center bg-[rgb(46,48,146)] text-white rounded-t-lg">
            <CardTitle className="flex items-center justify-center space-x-2">
              <Key className="h-5 w-5" />
              <span>Alterar a Senha</span>
            </CardTitle>
          </CardHeader>

          <CardContent className="p-6">
            <div className="space-y-6">
              <div className="text-center text-sm text-gray-600 mb-4">
                Documento verificado: <strong>{document}</strong>
              </div>

              <div>
                <Label htmlFor="new_password" className="text-sm font-medium text-gray-700">
                  Nova Senha
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="new_password"
                    type={showPasswords.new_password ? 'text' : 'password'}
                    placeholder="Digite sua nova senha"
                    value={passwords.new_password}
                    onChange={(e) => handlePasswordChange('new_password', e.target.value)}
                    className={`border-gray-300 focus:border-[rgb(46,48,146)] focus:ring-[rgb(46,48,146)] pr-10 ${
                      errors.new_password ? 'border-red-500' : ''
                    }`}
                    disabled={loading || success}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new_password')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPasswords.new_password ? (
                      <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
                {errors.new_password && (
                  <p className="text-red-500 text-xs mt-1">{errors.new_password}</p>
                )}
              </div>

              <div>
                <Label htmlFor="confirm_password" className="text-sm font-medium text-gray-700">
                  Repita a Senha
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="confirm_password"
                    type={showPasswords.confirm_password ? 'text' : 'password'}
                    placeholder="Repita sua nova senha"
                    value={passwords.confirm_password}
                    onChange={(e) => handlePasswordChange('confirm_password', e.target.value)}
                    className={`border-gray-300 focus:border-[rgb(46,48,146)] focus:ring-[rgb(46,48,146)] pr-10 ${
                      errors.confirm_password ? 'border-red-500' : ''
                    }`}
                    disabled={loading || success}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm_password')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPasswords.confirm_password ? (
                      <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
                {errors.confirm_password && (
                  <p className="text-red-500 text-xs mt-1">{errors.confirm_password}</p>
                )}
              </div>

              {/* Alertas de sucesso */}
              {success && (
                <Alert className="border-green-500 bg-green-50">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-700">
                    Senha alterada com sucesso! Redirecionando para o login...
                  </AlertDescription>
                </Alert>
              )}

              <Button
                onClick={handleUpdatePassword}
                disabled={loading || success}
                className="w-full bg-[rgb(46,48,146)] hover:bg-[rgb(46,48,146)]/90 text-white py-3 transition-colors duration-200"
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Salvando...</span>
                  </div>
                ) : success ? (
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="h-4 w-4" />
                    <span>Senha Alterada!</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <Key className="h-4 w-4" />
                    <span>Salvar Nova Senha</span>
                  </div>
                )}
              </Button>

              <div className="text-center">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  disabled={loading}
                  className="text-[rgb(46,48,146)] hover:bg-[rgb(46,48,146)]/10"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Voltar ao Login
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="text-center">
          <p className="text-xs text-gray-500">
            Mantenha sua senha segura e não a compartilhe com terceiros
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;